/**
 * Was neben einer Aufnahme im selben Ordner liegt — und wie man es erkennt.
 *
 * Vier Anhängsel tragen den **vollen** Namen ihrer Aufnahme samt Endung, mit einer weiteren
 * Endung dahinter: die Beglaubigung (`platform/signing.ts`), die Markierebene eines Fotos
 * (`platform/storage.ts::overlayPfad`), das Vorschaubild einer Videoaufnahme
 * (`platform/video.ts::thumbPfad`) und die Annotationsebene einer Videoaufnahme
 * (`platform/storage.ts::annotationsPfad`). `listFiles` liefert sie mit — sie liegen im Ordner
 * ihrer Art, nicht daneben.
 *
 * ⚠️ **Sie sind abgeleitet, nicht aufgenommen.** Wer sie über einen Kamm mit den Aufnahmen
 * schert, zählt sie mit: dann steht im Bogen ein zweites Foto, das niemand gemacht hat, und
 * seit dem 2026-09-14 geht diese falsche Zahl als `value` an den Relay-Server
 * (`domain/medienzahl.ts`).
 *
 * ⚠️ **Jedes Anhängsel verdirbt beim Zerlegen das letzte Feld des Namens.** Aus
 * `…_itemID=42.jpeg-overlay.svg` wird `itemID=42.jpeg-overlay`, aus `….mp4-thumb.jpg` ein
 * `taskID=…mp4-thumb`. Wer Felder aus einem Dateinamen liest, muss die Ableitung vorher
 * abschneiden — `ohneAbleitung` tut genau das, und `server/multipart.ts::felderAusName` ruft
 * es für alle Leser.
 *
 * **Hier statt in `platform/storage.ts`**, obwohl die Namen dort entstehen: dieses Wissen
 * brauchen auch Module, die mit dem Dateisystem nichts zu tun haben (`server/multipart.ts`)
 * oder die es bewusst nicht mitziehen wollen (`platform/archive.ts`, `server/anhaenge.ts`,
 * `server/classic/anforderungen.ts` hielten die Endungen deshalb bis zum 2026-09-14 je als
 * eigene Kopie — und die Kopien liefen auseinander, genau daran hing der Fehler). Ein reines
 * Modul ohne Abhängigkeiten kann jeder importieren; dieselbe Begründung wie bei
 * `domain/woerter.ts` und `domain/medienzahl.ts`.
 *
 * S. `memory/abgeleitete-dateien.md` — dort steht auch, welches Anhängsel auf welchem Gleis
 * hinausgeht.
 */

/** Die Beglaubigung einer Datei (`platform/signature.ts::sigPfad`). */
export const SIG_ENDE = '.sig';

/** Die Markierebene eines Fotos (`platform/storage.ts::overlayPfad`). */
export const OVERLAY_ENDE = '-overlay.svg';

/** Das Vorschaubild einer Videoaufnahme (`platform/video.ts::thumbPfad`). */
export const THUMB_ENDE = '-thumb.jpg';

/**
 * Die Annotationsebene einer Videoaufnahme (`platform/storage.ts::annotationsPfad`).
 *
 * ⚠️ **JSON, nicht SVG** — anders als beim Foto. Der Relay-Server soll daraus seine eigene
 * Ausgabe erzeugen (Ansicht, PDF, Einzelbilder je Änderungszeitpunkt) und bekommt deshalb die
 * Daten, kein fertiges Markup. Entscheidung des Nutzers vom 2026-09-20.
 */
export const ANNOT_ENDE = '-annotations.json';

/**
 * Die Anhängsel in der Reihenfolge, in der sie geprüft werden. `.sig` steht zuletzt, weil es
 * an **jede** der anderen Dateien noch einmal antreten kann (`foto.jpeg-overlay.svg.sig`).
 */
const ENDEN = [OVERLAY_ENDE, THUMB_ENDE, ANNOT_ENDE, SIG_ENDE] as const;

export const istBeglaubigung = (pfad: string) => pfad.endsWith(SIG_ENDE);
export const istMarkierebene = (pfad: string) => pfad.endsWith(OVERLAY_ENDE);
export const istVorschaubild = (pfad: string) => pfad.endsWith(THUMB_ENDE);
export const istVideoAnnotation = (pfad: string) => pfad.endsWith(ANNOT_ENDE);

/**
 * Ist diese Datei aus einer Aufnahme abgeleitet — also keine eigene?
 *
 * Für jede Liste von Aufnahmen die eine Frage: `persistence/repos.ts` baut daraus den
 * Kommentarkasten, `server/anhaenge.ts` und `server/classic/anforderungen.ts` entscheiden
 * damit, was hinausgeht.
 */
export function istAbgeleitet(pfad: string): boolean {
  return ENDEN.some((ende) => pfad.endsWith(ende));
}

/**
 * Der Name ohne seine Ableitung — für jeden, der Felder daraus liest.
 *
 * Mehrfach angewandt, weil sich die Anhängsel stapeln: die Beglaubigung einer Markierebene
 * heisst `…-overlay.svg.sig`, und nur die innerste Endung gehört zum eigentlichen Namen.
 */
export function ohneAbleitung(name: string): string {
  let rest = name;
  for (;;) {
    const ende = ENDEN.find((e) => rest.endsWith(e));
    if (!ende) return rest;
    rest = rest.slice(0, -ende.length);
  }
}
